import React from 'react';
import classNames from 'classnames';
import onClickOutside from 'react-onclickoutside';
import { MenuOptionGroup, MenuOption } from '../../types';
import { CarouselEditorCell } from './CarouselEditorCell';
import { EditOptionModal } from './EditOptionModal';
import './CarouselEditor.scss';

interface CarouselEditorProps {
  optionGroup: MenuOptionGroup;
  onClickAdd();
  updateOption({
    optionGroupId,
    oldOption,
    newOption,
  }: {
    optionGroupId: string;
    oldOption: MenuOption;
    newOption: MenuOption;
  });
  deleteOption({ optionGroupId, option }: { optionGroupId: string; option: MenuOption });
  moveOption(optionGroupId: string, dragIndex: number, hoverIndex: number);
  isPriceEditable: boolean;
}

interface CarouselEditorState {
  isAdding: boolean;
  editingOption: null | MenuOption;
  openId: MenuOption['option']['id'] | null;
}

class CarouselEditorUnwrapped extends React.Component<CarouselEditorProps, CarouselEditorState> {
  constructor(props) {
    super(props);

    this.state = {
      isAdding: false,
      editingOption: null,
      openId: null,
    };

    this.toggleModal = this.toggleModal.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleEditing = this.handleEditing.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.hideMenu = this.hideMenu.bind(this);
    this.openMenu = this.openMenu.bind(this);
  }

  hideMenu(option) {
    const id = option.option.id;

    if (id !== this.state.openId) {
      return;
    }

    this.setState({
      openId: null,
    });
  }

  handleClickOutside() {
    this.setState({ openId: null });
  }

  toggleModal() {
    this.setState({
      isAdding: false,
      editingOption: null,
    });
  }

  handleEditing(option) {
    this.setState({
      editingOption: option,
    });
  }

  handleUpdate(newOption) {
    const oldOption = this.state.editingOption!;
    this.setState({ editingOption: null }, () => {
      this.props.updateOption({
        optionGroupId: this.props.optionGroup.id,
        oldOption,
        newOption,
      });
    });
  }

  openMenu(option) {
    this.setState({ openId: option.option.id });
  }

  handleDelete = (option) => {
    this.props.deleteOption({ optionGroupId: this.props.optionGroup.id, option });
  };

  handleToggle = (option) => {
    this.props.updateOption({
      optionGroupId: this.props.optionGroup.id,
      oldOption: option,
      newOption: {
        ...option,
        enabled: !option.enabled,
      },
    });
  };

  onMoveOption = (dragIndex: number, hoverIndex: number) => {
    this.props.moveOption(this.props.optionGroup.id, dragIndex, hoverIndex);
  };

  renderAddButton() {
    return (
      <div>
        <a className='add-carousel-editor-button dashed-add-button' onClick={this.props.onClickAdd}>
          <div>
            <span>+</span>
            Add Brand
          </div>
        </a>
      </div>
    );
  }

  render() {
    const { optionGroup } = this.props;
    const config = optionGroup.editorConfig;
    return (
      <div className='mb-3'>
        <h3 className='drink-editor-heading'>{optionGroup.name}</h3>
        <div className={classNames('carousel-editor-row')}>
          {this.renderAddButton()}
          <div className='carousel-edit-row-options'>
            {optionGroup.menuOptions.map((menuOption, index) => (
              <CarouselEditorCell
                key={menuOption.option.id}
                index={index}
                option={menuOption}
                openMenu={this.openMenu}
                onToggle={this.handleToggle}
                onDelete={this.handleDelete}
                onEdit={this.handleEditing}
                menuOpen={this.state.openId === menuOption.option.id}
                hideMenu={this.hideMenu}
                moveCard={this.onMoveOption}
                isPriceEditable={this.props.isPriceEditable}
              />
            ))}
          </div>
        </div>
        {this.state.editingOption && (
          <EditOptionModal
            title={config.editText}
            name={optionGroup.name}
            option={this.state.editingOption}
            onCancel={this.toggleModal}
            onSubmit={this.handleUpdate}
          />
        )}
      </div>
    );
  }
}

export const CarouselEditor = onClickOutside(CarouselEditorUnwrapped) as React.ComponentType<CarouselEditorProps>;
